import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Cookie consent banner.
 *
 * Shows once, on first visit, until the visitor makes a choice. The choice is
 * persisted in localStorage so the banner does not reappear on later visits.
 *
 *   profio:cookie-consent = "all"        → accepted analytics / non-essential
 *                         = "essential"  → rejected non-essential (only strictly
 *                                          necessary cookies)
 *
 * Other code can read the choice via:  localStorage.getItem("profio:cookie-consent")
 * and a "profio:cookie-consent" window event is dispatched on change so
 * analytics scripts can react without a reload.
 */
const STORAGE_KEY = "profio:cookie-consent";

export function getCookieConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY); // "all" | "essential" | null
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  // Only decide visibility on the client, after mount, to avoid a flash.
  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  const choose = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
      window.dispatchEvent(
        new CustomEvent("profio:cookie-consent", { detail: value })
      );
    } catch {
      /* storage disabled — banner just closes for this session */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.banner.title", { defaultValue: "Cookies" })}
      className="fixed inset-x-0 bottom-[4.75rem] md:bottom-4 z-[60] px-3 md:px-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-[#20242f]/95 backdrop-blur shadow-2xl shadow-black/40 p-4 md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="font-semibold text-white text-sm md:text-base">
              {t("cookies.banner.title")}
            </p>
            <p className="mt-1 text-white/70 text-sm leading-relaxed">
              {t("cookies.banner.text")}{" "}
              <Link
                to="/cookies"
                className="text-[#98dea3] underline underline-offset-2 hover:text-white"
              >
                {t("cookies.banner.learnMore")}
              </Link>
            </p>
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => choose("essential")}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 transition"
            >
              {t("cookies.banner.reject")}
            </button>
            <button
              type="button"
              onClick={() => choose("all")}
              className="rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold text-white hover:brightness-110 transition"
            >
              {t("cookies.banner.accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
